import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  Mail,
  MessageSquareText,
  Mic,
  Phone,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

type IconType = ComponentType<{ className?: string }>;

const proofPoints = [
  { value: '24h', label: 'callbacks tracked before they go cold' },
  { value: '1', label: 'timeline for every customer conversation' },
  { value: '0', label: 'extra CRM busywork for the team' },
];

const problems = [
  'Missed calls never make it back to the right person.',
  'Quotes are sent, then nobody follows up.',
  'Customers repeat themselves because context lives in different inboxes.',
  'Managers only learn about dropped balls after the customer is already annoyed.',
];

const layers = [
  {
    title: 'Capture the work already happening',
    body: 'Calls, business-line recordings, emails, calendar events, and follow-ups are connected automatically.',
    icon: Phone,
  },
  {
    title: 'Turn it into customer context',
    body: 'Oracord builds one living timeline per customer so handoffs, promises, and next steps are obvious.',
    icon: MessageSquareText,
  },
  {
    title: 'Surface what needs attention',
    body: 'Owners see the follow-ups, coaching moments, and stalled conversations that need action now.',
    icon: BarChart3,
  },
];

const timelineSteps = [
  { label: 'Missed call', meta: 'Mon 9:42', icon: Phone },
  { label: 'Callback', meta: 'Mon 10:18', icon: MessageSquareText, active: true },
  { label: 'Quote sent', meta: 'Tue 2:04', icon: FileText },
  { label: 'Site visit', meta: 'Thu 11:00', icon: CalendarDays },
  { label: 'Follow-up due', meta: 'Today', icon: Clock3, warn: true },
];

const queueItems = [
  { name: 'Margaret Chen', note: 'Estimate call returned. Site visit booked. Quote follow-up due today.', tone: 'bg-amber-500' },
  { name: 'Industrial Pkwy', note: 'Commercial callback promised within 24 hours. No call logged yet.', tone: 'bg-red-500' },
  { name: 'Susan Park', note: 'Complaint resolved onsite. Confirmation call still open.', tone: 'bg-blue-500' },
];

const integrations = [
  { title: 'Business lines', body: 'Staff use Oracord numbers without exposing personal phones.', icon: Mic },
  { title: 'Gmail / Outlook', body: 'Follow quote and follow-up email activity.', icon: Mail },
  { title: 'Calendar', body: 'Connect calls and messages to booked appointments.', icon: CalendarDays },
  { title: 'Local Engine', body: 'Private processing runs on your company computer.', icon: ShieldCheck },
];

export default function Landing() {
  return (
    <main className="min-h-screen bg-[#f7f2e8] text-[#101827] antialiased">
      <header className="border-b border-[#101827]/10 bg-[#f7f2e8]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
          <Link to="/landing" className="inline-flex items-center gap-3 text-lg font-black tracking-tight">
            <LogoMark />
            Oracord
          </Link>
          <nav className="hidden items-center gap-7 text-sm font-bold text-slate-600 sm:flex">
            <a href="#product" className="hover:text-slate-950">Product</a>
            <a href="#privacy" className="hover:text-slate-950">Privacy</a>
            <a href="#pricing" className="hover:text-slate-950">Pricing</a>
          </nav>
          <a href="mailto:hello@oracord.com?subject=Oracord%20demo" className="rounded-full bg-[#101827] px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-slate-950/10 hover:bg-slate-800">
            Book a demo
          </a>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:py-24">
        <div>
          <div className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-800">
            Private communication intelligence
          </div>
          <h1 className="mt-6 max-w-3xl text-5xl font-black leading-[0.96] tracking-[-0.045em] text-slate-950 sm:text-6xl lg:text-7xl">
            Stop losing customers between the call, quote, and follow-up.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-650 sm:text-xl">
            Oracord gives service businesses one memory for every customer conversation — phone calls, emails, appointments, promises, and follow-ups — without forcing the team into another CRM.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href="mailto:hello@oracord.com?subject=Oracord%20demo" className="inline-flex items-center justify-center rounded-full bg-[#101827] px-6 py-3 text-sm font-black text-white shadow-xl shadow-slate-950/10 hover:bg-slate-800">
              Book a demo
            </a>
            <a href="#product" className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white/80 px-6 py-3 text-sm font-black text-slate-900 hover:border-slate-500">
              See the product
            </a>
          </div>
          <div className="mt-10 grid max-w-2xl grid-cols-3 gap-4 border-t border-slate-900/10 pt-6">
            {proofPoints.map(point => (
              <div key={point.label}>
                <div className="text-2xl font-black tracking-tight sm:text-3xl">{point.value}</div>
                <div className="mt-1 text-xs font-bold leading-5 text-slate-500 sm:text-sm">{point.label}</div>
              </div>
            ))}
          </div>
        </div>
        <ProductPreview />
      </section>

      <section className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="grid overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-950/5 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="bg-[#101827] p-8 text-white sm:p-10">
            <h2 className="text-3xl font-black tracking-[-0.03em] sm:text-4xl">This is not a reporting problem. It is a memory problem.</h2>
            <p className="mt-5 text-base leading-7 text-slate-300">
              Most small teams already have the right tools. The problem is that none of those tools remember the whole customer story.
            </p>
          </div>
          <div className="grid gap-0 divide-y divide-slate-100 p-4 sm:p-6 md:grid-cols-2 md:divide-x md:divide-y-0">
            {problems.map(problem => (
              <div key={problem} className="flex gap-4 p-5">
                <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-red-500" />
                <p className="text-sm font-bold leading-7 text-slate-700 sm:text-base">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="product" className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <div className="lg:sticky lg:top-24">
            <div className="text-sm font-black uppercase tracking-[0.22em] text-blue-700">How Oracord works</div>
            <h2 className="mt-4 text-4xl font-black leading-tight tracking-[-0.035em] text-slate-950 sm:text-5xl">One timeline. Every handoff. Clear next steps.</h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              Oracord watches the operational trail your team already creates and turns it into customer context the business can act on.
            </p>
          </div>
          <div className="grid gap-4">
            {layers.map((layer, index) => <LayerCard key={layer.title} index={index + 1} {...layer} />)}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-5 py-14 sm:px-8 lg:grid-cols-[1fr_0.9fr] lg:items-center">
        <TimelineCard />
        <div>
          <div className="text-sm font-black uppercase tracking-[0.22em] text-amber-700">Follow-up queue</div>
          <h2 className="mt-4 text-4xl font-black leading-tight tracking-[-0.035em] sm:text-5xl">Know what is slipping before the customer does.</h2>
          <p className="mt-5 text-lg leading-8 text-slate-600">
            Missed callbacks, stale quotes, unresolved complaints, and promised next steps surface automatically. No spreadsheet. No manager chasing everyone for updates.
          </p>
          <div className="mt-7 space-y-3">
            {queueItems.map(item => (
              <div key={item.name} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex gap-3">
                  <span className={`mt-2 h-2.5 w-2.5 shrink-0 rounded-full ${item.tone}`} />
                  <div>
                    <div className="font-black text-slate-950">{item.name}</div>
                    <p className="mt-1 text-sm leading-6 text-slate-600">{item.note}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="privacy" className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-8 rounded-[2rem] border border-emerald-200 bg-emerald-50 p-8 shadow-xl shadow-emerald-950/5 sm:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <div className="text-sm font-black uppercase tracking-[0.22em] text-emerald-800">Privacy first</div>
            <h2 className="mt-4 text-4xl font-black leading-tight tracking-[-0.035em] sm:text-5xl">Built around a local Engine, not a cloud data grab.</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {integrations.map(integration => <IntegrationCard key={integration.title} {...integration} />)}
          </div>
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="overflow-hidden rounded-[2rem] bg-[#101827] text-white shadow-2xl shadow-slate-950/20">
          <div className="grid gap-8 p-8 sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <h2 className="max-w-3xl text-4xl font-black leading-tight tracking-[-0.035em] sm:text-5xl">Give the business a communication memory.</h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                Start with the owner, add the team, connect the local Engine, and let Oracord catch the dropped balls before they cost you work.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <a href="mailto:hello@oracord.com?subject=Oracord%20demo" className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-black text-slate-950 hover:bg-slate-100">
                Book a demo
              </a>
              <Link to="/billing" className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-black text-white hover:bg-white/10">
                View pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 px-5 py-8 text-sm text-slate-500 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>© 2026 Oracord</div>
          <div>Private, local-first communication intelligence</div>
        </div>
      </footer>
    </main>
  );
}

function ProductPreview() {
  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-2xl shadow-slate-950/10 sm:p-5">
      <div className="rounded-[1.5rem] bg-[#101827] p-5 text-white">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <div className="text-sm font-black text-white">Customer timeline</div>
            <div className="mt-1 text-xs font-semibold text-slate-400">Margaret Chen · landscaping estimate</div>
          </div>
          <span className="rounded-full bg-amber-400/15 px-3 py-1 text-xs font-black text-amber-200">Follow-up due</span>
        </div>
        <div className="mt-5 grid gap-3">
          {timelineSteps.map(step => {
            const Icon = step.icon;
            return (
              <div key={step.label} className={`flex items-center gap-4 rounded-2xl border p-4 ${step.active ? 'border-blue-400/40 bg-blue-400/10' : step.warn ? 'border-amber-400/40 bg-amber-400/10' : 'border-white/10 bg-white/5'}`}>
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${step.active ? 'bg-blue-400 text-white' : step.warn ? 'bg-amber-300 text-slate-950' : 'bg-white/10 text-slate-300'}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-black">{step.label}</div>
                  <div className="mt-0.5 text-xs font-semibold text-slate-400">{step.meta}</div>
                </div>
                {step.warn ? <CheckCircle2 className="h-5 w-5 text-amber-300" /> : null}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <MiniPanel title="Summary" body="Customer wants backyard regrade, new sod, and patio quote. Site visit happened Thursday." />
        <MiniPanel title="Next step" body="Send quote follow-up before 4pm. Mention crew availability for next week." />
      </div>
    </div>
  );
}

function TimelineCard() {
  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-950/5 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-black">Today’s risk queue</div>
          <div className="mt-1 text-sm font-semibold text-slate-500">Generated from calls, email, and calendar</div>
        </div>
        <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-black text-red-700">3 need action</span>
      </div>
      <div className="mt-6 space-y-3">
        <RiskRow title="Quote sent, no reply" meta="Dave Morrison · 5 days open" />
        <RiskRow title="Callback promised" meta="Industrial Pkwy · 28 hours overdue" urgent />
        <RiskRow title="Complaint confirmation" meta="Susan Park · resolved, not closed" />
      </div>
    </div>
  );
}

function LogoMark() {
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#101827] text-white shadow-sm">
      <Sparkles className="h-4 w-4" />
    </span>
  );
}

function LayerCard({ index, title, body, icon: Icon }: { index: number; title: string; body: string; icon: IconType }) {
  return (
    <div className="group rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-950/5">
      <div className="flex items-start gap-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Layer {index}</div>
          <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950">{title}</h3>
          <p className="mt-3 text-base leading-7 text-slate-600">{body}</p>
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ title, body, icon: Icon }: { title: string; body: string; icon: IconType }) {
  return (
    <div className="rounded-2xl border border-emerald-200 bg-white/80 p-5">
      <Icon className="h-5 w-5 text-emerald-800" />
      <h3 className="mt-4 font-black text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </div>
  );
}

function MiniPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-[#fbf8f2] p-4">
      <div className="text-sm font-black text-slate-950">{title}</div>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </div>
  );
}

function RiskRow({ title, meta, urgent = false }: { title: string; meta: string; urgent?: boolean }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-[#fbf8f2] p-4">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${urgent ? 'bg-red-500' : 'bg-amber-500'}`} />
      <div>
        <div className="font-black text-slate-950">{title}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{meta}</div>
      </div>
    </div>
  );
}
